// filepath: apps/web/src/features/admin/hmacKeys/HmacKeysTable.tsx
//
// FR-UI-ADM-7 AC-1/AC-6/AC-7/AC-8/AC-9 — table component for the
// HMAC key rotation action.
//
// Renders one row per WebhookKeyMeta with:
//   - state indicator (green: active / yellow: grace_period / red: expired)
//   - key_id, created_at, grace_until_countdown (only for grace_period), expired_at
//   - revoke action button (disabled for revoked keys)
//
// The countdown timer is a local useEffect + setInterval that ticks
// every 1s. It does NOT call the backend (no polling).

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { WebhookKeyMeta } from "./types";

interface HmacKeysTableProps {
  keys: WebhookKeyMeta[];
  onRotateClick: () => void;
  onRevokeClick: (key: WebhookKeyMeta) => void;
}

function stateIndicatorClass(state: WebhookKeyMeta["state"]): string {
  switch (state) {
    case "active":
      return "hmac-keys-state hmac-keys-state--active";
    case "grace_period":
      return "hmac-keys-state hmac-keys-state--grace";
    case "expired":
      return "hmac-keys-state hmac-keys-state--expired";
  }
}

function stateIndicatorGlyph(state: WebhookKeyMeta["state"]): string {
  switch (state) {
    case "active":
      return "\u25CF"; // ● green
    case "grace_period":
      return "\u25CF"; // ● yellow
    case "expired":
      return "\u25CF"; // ● red
  }
}

function formatUtc(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d
      .toISOString()
      .replace("T", " ")
      .replace(/\.\d+Z$/, " UTC");
  } catch {
    return iso;
  }
}

function formatRemaining(graceUntilIso: string, now: number): string {
  const t = Date.parse(graceUntilIso);
  if (Number.isNaN(t)) return graceUntilIso;
  const diff = t - now;
  if (diff <= 0) return "0s";
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/**
 * CountdownCell -- a live-updating countdown string for grace_until.
 * Re-renders every 1s; clears the interval on unmount.
 */
function CountdownCell({
  graceUntilIso,
}: {
  graceUntilIso: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = formatRemaining(graceUntilIso, now);
  return (
    <span data-testid="grace-countdown" className="hmac-keys-countdown">
      {remaining}{" "}
      <small>
        {t("admin.webhooks.keys.countdown.separator")}{" "}
        {formatUtc(graceUntilIso)}
      </small>
    </span>
  );
}

export function HmacKeysTable({
  keys,
  onRotateClick: _onRotateClick,
  onRevokeClick,
}: HmacKeysTableProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <table data-testid="hmac-keys-table" className="hmac-keys-table">
      <thead>
        <tr>
          <th>{t("admin.webhooks.keys.state_label")}</th>
          <th>{t("admin.webhooks.keys.key_id_label")}</th>
          <th>{t("admin.webhooks.keys.created_at_label")}</th>
          <th>{t("admin.webhooks.keys.grace_until_label")}</th>
          <th>{t("admin.webhooks.keys.expired_at_label")}</th>
          <th>{t("admin.webhooks.keys.revoke_action")}</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => (
          <tr
            key={k.key_id}
            data-testid={`hmac-key-row-${k.key_id}`}
            data-state={k.state}
          >
            <td>
              <span
                className={stateIndicatorClass(k.state)}
                aria-label={t(`admin.webhooks.keys.${k.state}`)}
                data-testid={`hmac-key-state-${k.key_id}`}
              >
                {stateIndicatorGlyph(k.state)}{" "}
                {t(`admin.webhooks.keys.${k.state}`)}
              </span>
            </td>
            <td>
              <code data-testid={`hmac-key-id-${k.key_id}`}>{k.key_id}</code>
            </td>
            <td data-testid={`hmac-key-created-${k.key_id}`}>
              {formatUtc(k.created_at)}
            </td>
            <td>
              {k.state === "grace_period" && k.grace_until ? (
                <CountdownCell graceUntilIso={k.grace_until} />
              ) : (
                <span aria-hidden="true">&mdash;</span>
              )}
            </td>
            <td>
              {k.state === "expired" && k.expired_at ? (
                <span data-testid={`hmac-key-expired-${k.key_id}`}>
                  {t("admin.webhooks.keys.expired_at_label")}{" "}
                  {formatUtc(k.expired_at)}
                </span>
              ) : (
                <span aria-hidden="true">&mdash;</span>
              )}
            </td>
            <td>
              <button
                type="button"
                disabled={k.state === "expired"}
                onClick={() => onRevokeClick(k)}
                data-testid={`hmac-key-revoke-${k.key_id}`}
              >
                {t("admin.webhooks.keys.revoke_action")}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
